/**
 * EventProvidersSection - Shows relevant event service providers on subcategory pages
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Star, MapPin, ChevronRight, Users, Building, 
  Utensils, Camera, Music, Palette, Package, ClipboardList,
  Loader2, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map category slugs to event provider service types (matching database format)
const CATEGORY_TO_SERVICE_TYPE = {
  'event-venues': 'event-venues',
  'catering-services': 'catering-services',
  'djs-bands': 'djs-bands',
  'photography-videography': 'photography-videography',
  'event-decoration': 'event-decoration',
  'party-equipment-hire': 'party-equipment-hire',
  'event-planning': 'event-planning',
};

// Category icons
const CATEGORY_ICONS = {
  'event-venues': Building,
  'catering-services': Utensils,
  'djs-bands': Music,
  'photography-videography': Camera,
  'event-decoration': Palette,
  'party-equipment-hire': Package,
  'event-planning': ClipboardList,
};

// Service type colors
const SERVICE_COLORS = {
  'event-venues': 'from-purple-500 to-indigo-600',
  'catering-services': 'from-orange-500 to-red-500',
  'djs-bands': 'from-cyan-500 to-blue-600',
  'photography-videography': 'from-pink-500 to-rose-600',
  'event-decoration': 'from-teal-500 to-emerald-600',
  'party-equipment-hire': 'from-amber-500 to-orange-600',
  'event-planning': 'from-blue-500 to-indigo-600',
};

function ProviderCard({ provider, currencySymbol, onClick }) {
  const navigate = useNavigate();
  const serviceType = provider.service_type?.toLowerCase().replace(/_/g, '-') || 'event-venues';
  const Icon = CATEGORY_ICONS[serviceType] || Building;
  const gradientClass = SERVICE_COLORS[serviceType] || 'from-gray-500 to-gray-600';
  
  const handleClick = () => {
    navigate(`/events/book/${provider.id}`);
  };
  
  return (
    <Card 
      className="group hover:shadow-lg transition-all cursor-pointer border border-gray-200 hover:border-purple-300"
      onClick={handleClick}
      data-testid={`provider-${provider.id}`}
    >
      <div className="relative h-32 overflow-hidden rounded-t-lg">
        {provider.images?.[0] ? (
          <img 
            src={provider.images[0]} 
            alt={provider.provider_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-r ${gradientClass} flex items-center justify-center`}>
            <Icon className="w-12 h-12 text-white/50" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {provider.featured && (
          <Badge className="absolute top-2 left-2 bg-purple-500 text-white">
            Featured
          </Badge>
        )}
        
        {provider.verified && (
          <Badge className="absolute top-2 right-2 bg-green-500 text-white">
            Verified
          </Badge>
        )}
      </div>
      
      <CardContent className="pt-3 pb-4">
        <h3 className="font-bold text-base line-clamp-1">{provider.provider_name}</h3>
        
        <p className="text-xs text-gray-500 uppercase tracking-wide mt-1">
          {provider.service_type?.replace(/_/g, ' ')}
        </p>
        
        <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
          {provider.rating > 0 && (
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              {provider.rating?.toFixed(1)}
              {provider.review_count > 0 && (
                <span className="text-gray-400">({provider.review_count})</span>
              )}
            </span>
          )}
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {provider.location || provider.city}
          </span>
        </div>
        
        {/* Event types */}
        {provider.event_types?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {provider.event_types.slice(0, 2).map((type, idx) => (
              <Badge key={idx} variant="outline" className="text-xs capitalize">
                {type}
              </Badge>
            ))}
          </div>
        )}
        
        <div className="flex items-center justify-between mt-3 pt-2 border-t">
          <div>
            <p className="text-xs text-gray-500">From</p>
            <p className="font-bold text-purple-600">
              {currencySymbol}{provider.starting_price?.toLocaleString() || 'Contact'}
            </p>
          </div>
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
            View
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EventProvidersSection({ categorySlug, country = 'SL', maxProviders = 4 }) {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  // Get service type(s) for this category
  const serviceTypes = CATEGORY_TO_SERVICE_TYPE[categorySlug];
  
  useEffect(() => {
    const fetchProviders = async () => {
      if (!serviceTypes) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Handle array of service types (e.g., DJ and BAND)
        const typesToFetch = Array.isArray(serviceTypes) ? serviceTypes : [serviceTypes];
        
        const allProviders = [];
        for (const type of typesToFetch) {
          const response = await fetch(
            `${API_URL}/api/events/providers?service_type=${type}&country=${country}&limit=${maxProviders}`
          );
          if (response.ok) {
            const data = await response.json();
            allProviders.push(...(data.providers || []));
          }
        }
        
        // Remove duplicates and limit
        const uniqueProviders = allProviders.filter(
          (provider, index, self) => index === self.findIndex(p => p.id === provider.id)
        ).slice(0, maxProviders);
        
        setProviders(uniqueProviders);
      } catch (error) {
        console.error('Failed to fetch event providers:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProviders();
  }, [categorySlug, country, serviceTypes, maxProviders]);
  
  // Don't render if not an event category or no providers
  if (!serviceTypes) return null;
  
  if (loading) {
    return (
      <div className="py-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Sparkles className="w-5 h-5 text-purple-600" />
          </div>
          <h2 className="text-xl font-bold">Service Providers</h2>
        </div>
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </div>
    );
  }
  
  if (providers.length === 0) return null;
  
  return (
    <div className="py-6" data-testid="event-providers-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Sparkles className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Service Providers</h2>
            <p className="text-sm text-gray-500">Professional event services available</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/category/events-functions')}
          className="text-purple-600 border-purple-200 hover:bg-purple-50"
        >
          View All Services
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {providers.map(provider => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            currencySymbol={currencySymbol}
          />
        ))}
      </div>
    </div>
  );
}

// Export helper function to check if a category has event providers
export function isEventServiceCategory(slug) {
  return slug in CATEGORY_TO_SERVICE_TYPE;
}
